import Cookies from "js-cookie";
import React, { useEffect, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useRefreshToken } from "../hooks/mutations/login-mutation";
import XuronLogo from "../img/logo2.svg";
import { User } from "@gravity-ui/uikit";
import { useAuth } from "../context/AuthContext";
import { ChevronDown, FileSpreadsheet, LogOut, Menu, ShieldCheck, X } from "lucide-react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import bg from "../img/mainbg.jpg";

const RootLayoutUser = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { updateAuth } = useAuth();
  const outletRef = useRef<HTMLDivElement>(null);
  const [searchParams] = useSearchParams();
  const [menuOpen, setMenuOpen] = useState(false);
  const { firstName, lastName, email, clearAuth } = useAuth();

  const refreshTokenMutation = useRefreshToken({
    onSuccess: (res) => {
      clearAuth();
      Cookies.set("access", res?.access as string, { expires: 1 });
      Cookies.set("refresh", res?.refresh as string, { expires: 30 });
      Cookies.set("role", res?.user?.role as string, { expires: 30 });
      Cookies.set("org_id", res?.user?.org_id?.toString() as string, {
        expires: 30,
      });
      Cookies.set("user_id", res?.user?.id?.toString() as string, {
        expires: 30,
      });
      Cookies.set("first_name", res?.user?.firstName?.toString() as string, {
        expires: 30,
      });
      Cookies.set("last_name", res?.user?.lastName?.toString() as string, {
        expires: 30,
      });
      Cookies.set("email", res?.user?.email?.toString() as string, {
        expires: 30,
      });
      updateAuth(
        res.user.role as any,
        String(res.user.org_id),
        String(res.user.org_name),
        String(res.user.id),
        res.user.firstName,
        res.user.lastName,
        String(res.user.email),
        true
      );
      const backto = searchParams.get("backto");
      if (backto) {
        navigate(decodeURIComponent(backto).replace(/^\/+/, "/"), { replace: true });
      } else {
        navigate("/home");
      }
    },
    onError: (error) => {
      console.log(error);
    },
  });

  useEffect(() => {
    const accessToken = Cookies.get("access");
    const refreshToken = Cookies.get("refresh");

    if (refreshToken && !accessToken) {
      refreshTokenMutation.mutate({ refreshToken });
    }
  }, [navigate, location.pathname]);

  // Close mobile menu on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // ESC to close mobile menu
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setMenuOpen(false);
    if (menuOpen) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  return (
    <div className="flex h-screen w-screen flex-col overflow-hidden bg-white">
      {/* Top navbar */}
      <header className="sticky top-0 z-40 border-b border-gray-200 bg-white">
        <div className="mx-auto flex h-14 items-center justify-between px-4 sm:px-6 md:px-10">
          {/* Brand */}
          <div className="flex items-center gap-2">
            <img src={XuronLogo} className="h-5 w-auto sm:h-6 md:h-7" alt="Xuron" />
          </div>

          {/* Desktop: User avatar + dropdown */}
          <div className="hidden sm:flex items-center gap-2">
            <User
              avatar={{ text: `${firstName ?? ""} ${lastName ?? ""}`, theme: "brand" }}
              name={`${firstName ?? ""} ${lastName ?? ""}`}
              description={email ?? ""}
              size="m"
            />

            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <button className="">
                  <ChevronDown className="text-gray-500 w-5 h-5" />
                </button>
              </DropdownMenu.Trigger>

              <DropdownMenu.Content
                sideOffset={15}
                align="end"
                className="min-w-[180px] rounded-xl p-1 bg-white shadow-lg"
              >
                <DropdownMenu.Item className="p-2.5 cursor-pointer">
                  <User
                    avatar={{ text: `${firstName ?? ""} ${lastName ?? ""}`, theme: "brand" }}
                    name={`${firstName ?? ""} ${lastName ?? ""}`}
                    description={email ?? ""}
                    size="m"
                  />
                </DropdownMenu.Item>
                <DropdownMenu.Separator className="h-px bg-gray-200 my-1" />
                <DropdownMenu.Item className="p-3 py-2 cursor-pointer">
                  <a
                    href="https://xuron.io/privacy-policy/"
                    className="flex items-center gap-1.5 text-sm font-medium"
                    target="_blank"
                  >
                    <ShieldCheck className="w-4 h-4 text-gray-500" /> Privacy Policy
                  </a>
                </DropdownMenu.Item>
                <DropdownMenu.Item className="p-3 py-2 cursor-pointer">
                  <a
                    href="https://xuron.io/terms-of-service/"
                    className="flex items-center gap-1.5 text-sm font-medium"
                    target="_blank"
                  >
                    <FileSpreadsheet className="w-4 h-4 text-gray-500" /> Terms of service
                  </a>
                </DropdownMenu.Item>

                <DropdownMenu.Separator className="h-px bg-gray-200 my-1" />
                <DropdownMenu.Item className="p-3 py-2 cursor-pointer">
                  <button
                    onClick={() => {
                      clearAuth();
                      navigate("/");
                    }}
                    type="button"
                    className="flex w-full gap-1.5 text-sm font-medium items-center text-red-500"
                  >
                    <LogOut className="w-4 h-4" /> Log Out
                  </button>
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Root>
          </div>

          {/* Mobile: Burger button */}
          <button
            className="sm:hidden p-2 rounded-md text-gray-600 hover:bg-gray-100"
            onClick={() => setMenuOpen(true)}
          >
            <Menu className="w-6 h-6" />
          </button>
        </div>
      </header>

      {/* Mobile menu overlay */}
      <div
        className={`fixed inset-0 z-50 flex transition-opacity duration-300 ${
          menuOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      >
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black/40 transition-opacity duration-300" onClick={() => setMenuOpen(false)} />

        {/* Slide-out panel */}

        {/* Slide-out panel */}
        <div
          className={`relative ml-auto h-full w-64 bg-white shadow-lg p-4 flex flex-col gap-4
  transform transition-transform duration-300 ease-in-out
  ${menuOpen ? "translate-x-0" : "translate-x-full"}`}
        >
          <button
            className="absolute top-3 right-3 text-gray-600 hover:text-gray-900"
            onClick={() => setMenuOpen(false)}
          >
            <X className="w-6 h-6" />
          </button>

          {/* User info */}
          <User
            avatar={{ text: `${firstName ?? ""} ${lastName ?? ""}`, theme: "brand" }}
            name={`${firstName ?? ""} ${lastName ?? ""}`}
            description={email ?? ""}
            size="l"
          />

          {/* Links */}
          <nav className="flex flex-col gap-2 mt-4">
            <a
              href="https://xuron.io/privacy-policy/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-gray-50 text-sm font-medium text-gray-700"
            >
              <ShieldCheck className="w-4 h-4 text-gray-500" /> Privacy Policy
            </a>

            <a
              href="https://xuron.io/terms-of-service/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-gray-50 text-sm font-medium text-gray-700"
            >
              <FileSpreadsheet className="w-4 h-4 text-gray-500" /> Terms of Service
            </a>

            <button
              onClick={() => {
                navigate("/");
                clearAuth();
              }}
              className="w-full flex items-center gap-2 px-4 py-2 rounded-md bg-red-50 text-red-600 font-medium hover:bg-red-100"
            >
              <LogOut className="w-4 h-4" /> Log Out
            </button>
          </nav>

          {/* Spacer pushes log out down */}
          <div className="flex-1" />

          {/* Logout */}
        </div>
      </div>

      {/* Main content */}
      <main className="min-w-0 flex-1 overflow-hidden">
        <div
          ref={outletRef}
          className="h-full overflow-y-auto pb-4"
          style={{
            backgroundImage: location.pathname === "/home" ? `url(${bg})` : "none",
            backgroundColor: location.pathname === "/home" ? "transparent" : "white",
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div className="max-w-[1224px] mx-auto">
            <Outlet />
          </div>
        </div>
      </main>
    </div>
  );
};

export default RootLayoutUser;
